import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
// i18next
import { useTranslation } from 'react-i18next';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import AiChatAsync from 'store/aiChat/aiChatAsync';
import { aiChatActions } from 'store/aiChat/aiChatSlice';
import { selectTranslations } from 'store/aiChat/aiChatSelectors';
import { selectQuiz } from 'store/quiz/quizzesSelectors';
import IQuestion from 'models/Question';
import IBucket from 'models/Bucket';
import IPostQuizPage from 'models/PostQuizPage';
import QuizTypes from 'types/QuizTypes';
// Layouts
import { Dialog, DialogTitle, DialogContent, DialogActions } from 'layouts/Dialog';
// Components
import { Button, IconButton } from 'components/Buttons';
import { countries } from 'components/LanguageSelect';
import { Input } from 'components/Controls';
// MUI
import { Tooltip } from '@material-ui/core';
// Icons
import { FaTrash, FaEdit, FaCheck } from 'react-icons/fa';

type Props = {
  open: boolean;
  onClose: () => void;
  language: string;
}

const GenerateTranslationsDialog:React.FC<Props> = ({ open, onClose, language }) => {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();

  const fields = useMemo(() => [
    { label: t('ownerPage.quizForm.brandName'), key: 'brand' },
    { label: t('ownerPage.quizForm.registrationCaption'), key: 'registrationPage.caption' },
    { label: t('dialogs.generateTranslations.homeButtonTitle'), key: 'landingPage.callToAction.buttonTitle' },
    { label: t('ownerPage.quizForm.displayName'), key: 'displayName' },
    { label: t('dialogs.generateTranslations.waiverText'), key: 'waiverText' }
  ], [t]);

  const { watch, getValues, setValue } = useFormContext();

  const lng = watch('language');

  // with useMemo watchedTranslations is not updated after using setValue
  // eslint-disable-next-line
  const watchedTranslations = watch('translations') || {};

  const quiz = useAppSelector(selectQuiz);
  const translations = useAppSelector(selectTranslations);

  const [isLoading, setIsLoading] = useState(false);
  const [initialTranslations, setInitialTranslations] = useState<any>({ [lng]: {} });
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [selectedKeyValue, setSelectedKeyValue] = useState<string>('');

  const isScoringQuiz = quiz && [QuizTypes.Prediction, QuizTypes.Scoring].includes(quiz.type);

  const getInitialTranslations = () => {
    const translations:Record<string, any> = { [lng]: {} };

    if (isScoringQuiz) {
      fields.push(...[
        { label: t('dialogs.generateTranslations.ctaButtonTitle', { number: '' }), key: 'proceedButtonTitle' },
        { label: t('dialogs.generateTranslations.ctaText', { number: '' }), key: 'proceedText' },
        { label: t('dialogs.generateTranslations.liveLeaderboardHeaderText'), key: 'publicLeaderboard.headerText' },
        { label: t('dialogs.generateTranslations.liveLeaderboardQrCodeTitle'), key: 'publicLeaderboard.quizQRCodeTitle' }
      ]);
    }

    if (quiz?.type === QuizTypes.Personality) {
      fields.splice(3, 0, { label: t('dialogs.generateTranslations.homeScreenText'), key: 'landingPage.callToAction.text' });

      getValues('buckets').forEach((bucket: IBucket, index: number) => {
        const key = `buckets[${bucket.ref}].title`;
        translations[lng] = { ...translations[lng], [key]: bucket.title };
        fields.push({ label: `${t('dialogs.generateTranslations.bucket')} ${index + 1}`, key });
      })

      getValues('postQuizPages').forEach((postQuizPage: IPostQuizPage, index: number) => {
        const buttonTitleKey = `postQuizPages[${index}].callToAction.buttonTitle`;
        const textKey = `postQuizPages[${index}].callToAction.text`;

        translations[lng] = { ...translations[lng], [buttonTitleKey]: postQuizPage.callToAction.buttonTitle };
        fields.push({ label: t('dialogs.generateTranslations.ctaButtonTitle', { number: `${index + 1}` }), key: buttonTitleKey });

        translations[lng] = { ...translations[lng], [textKey]: postQuizPage.callToAction.text };
        fields.push({ label: t('dialogs.generateTranslations.ctaText', { number: `${index + 1}` }), key: textKey });
      })
    }
  
    fields.forEach(field => {
      if (!getValues(field.key)) return;
      translations[lng] = { ...translations[lng], [field.key]: getValues(field.key) };
    })

    const spotPrizes = getValues('spotPrizes');
    if (spotPrizes) {
      translations[lng] = { ...translations[lng], spotPrizes };
      fields.push({ label: t('common.randomPrize'), key: 'spotPrizes' })
    }

    getValues('prizes').forEach((prize:any, index: number) => {
      const key = `prizes.${index}.prize`;
      translations[lng] = { ...translations[lng], [key]: prize.prize };
      fields.push({ label: `${t('common.prize')} ${index + 1}`, key });
    })

    getValues('questions').forEach((question: IQuestion, index: number) => {
      const key = `questions.${question.ref}.title`;
      translations[lng] = { ...translations[lng], [key]: question.title };
      fields.push({ label: `${t('common.question')} ${index + 1}`, key });

      question.answers.forEach((answer, i) => {
        const key = `questions[${question.ref}].answers.${answer.ref}.title`;
        translations[lng] = { ...translations[lng], [key]: answer.title };
        fields.push({ label: `${t('common.answer')} ${i + 1}`, key });
      })
    })

    if (isScoringQuiz) {
      const postQuizPageQuestions = getValues('postQuizPages')?.[0]?.questions;

      if (postQuizPageQuestions) {
        postQuizPageQuestions.forEach((question: IQuestion, index: number) => {
          const key = `postQuizPages[0].questions.${question.ref}.title`;
          translations[lng] = { ...translations[lng], [key]: question.title };
          fields.push({ label: `${t('common.question')} ${index + 1}`, key });
    
          question.answers.forEach((answer, i) => {
            const key = `postQuizPages[0].questions[${question.ref}].answers.${answer.ref}.title`;
            translations[lng] = { ...translations[lng], [key]: answer.title };
            fields.push({ label: `${t('common.answer')} ${i + 1}`, key });
          })
        })
      }

      const preQuizPageQuestions = getValues('preQuizPages')?.[0]?.questions;

      if (preQuizPageQuestions) {
        preQuizPageQuestions.forEach((question: IQuestion, index: number) => {
          const key = `preQuizPages[0].questions.${question.ref}.title`;
          translations[lng] = { ...translations[lng], [key]: question.title };
          fields.push({ label: `${t('common.question')} ${index + 1}`, key });
    
          question.answers.forEach((answer, i) => {
            const key = `preQuizPages[0].questions[${question.ref}].answers.${answer.ref}.title`;
            translations[lng] = { ...translations[lng], [key]: answer.title };
            fields.push({ label: `${t('common.answer')} ${i + 1}`, key });
          })
        })
      }
    }
    
    setInitialTranslations(translations);
  }

  const nextTranslations = useMemo(() => {
    const result:any = { [lng]: {} };

    Object.keys(initialTranslations[lng]).filter(key => !watchedTranslations[language]?.[key]).forEach(field => {
      result[lng][field] = initialTranslations[lng][field]
    })

    return result;
  }, [initialTranslations, language, lng, watchedTranslations]);
  
  const handleApply = () => {
    setValue('translations', {
      ...watchedTranslations,
      [language]: {
        ...(watchedTranslations[language] || {}),
        ...(translations?.[language] || {}),
      }
    })
    onClose();
  }

  const handleTranslate = () => {
    setIsLoading(true);
    dispatch(AiChatAsync.generateTranslations({ language, translations: nextTranslations })).unwrap()
      .finally(() => setIsLoading(false));
  }

  const handleRemove = (key: string) => {
    setInitialTranslations((prev:any) => {
      const { [key]:_, ...otherTranslations } = prev[lng]
      return ({ ...prev, [lng]: otherTranslations });
    });
  }

  const handleEdit = (field:string) => {
    const value = translations?.[language]?.[field] ? translations[language][field] : watchedTranslations?.[language]?.[field] || '';
    setSelectedKey(field);
    setSelectedKeyValue(value);
  }

  const handleSave = (field: string) => {
    if (!selectedKey) return;

    if (translations?.[language]?.[field] !== undefined) {
      dispatch(aiChatActions.changeTranslationField({ lng: language, field, value: selectedKeyValue }));
    } else {
      setValue('translations', {
        ...watchedTranslations,
        [language]: {
          ...(watchedTranslations[language] || {}),
          [field]: selectedKeyValue
        }
      })
    }

    setSelectedKey(null);
    setSelectedKeyValue('');
  }

  useEffect(() => {
    getInitialTranslations();
    
    return () => {
      dispatch(aiChatActions.setInitialField('translations'));
    }
    // eslint-disable-next-line
  }, []);

  const isEmptyToTranslate = Object.keys(nextTranslations[lng]).length === 0;

  return (
    <Dialog size="xl" open={open} onClose={onClose}>
      <div className="flex flex-grow flex-col overflow-y-auto">
        <DialogTitle
          title={t('dialogs.generateTranslations.aiTranslate')}
          onClose={onClose}
        >
          <span className="block text-gray-400 text-sm font-light leading-5">
            {t('dialogs.generateTranslations.dialogHint', { language: countries.find(c => c.value === language)?.label || language })}
          </span>
        </DialogTitle>
        <DialogContent>
          <table>
            <thead>
              <tr>
                <th className="text-left">{t('dialogs.generateTranslations.label')}</th>
                <th className="text-left">{t('dialogs.generateTranslations.value')}</th>
                <th className="text-left">{t('dialogs.generateTranslations.translation')}</th>
                <th className="text-right">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {Object.keys(initialTranslations[lng]).map(field => (
                <tr key={field}>
                  <td className="text-left title w-3/12">{fields.find(f => f.key === field)?.label || ''}</td>
                  <td className="text-left title w-4/12">{initialTranslations[lng][field]}</td>
                  <td className="text-left title w-4/12" onDoubleClick={() => handleEdit(field)}>
                    {selectedKey === field ? (
                      <Input
                        id="edit-translation-input" name="edit-translation-input"
                        className="mb-0"
                        value={selectedKeyValue}
                        onChange={(e) => setSelectedKeyValue(e.target.value)}
                        onKeyPress={e => {
                          if (e.key === 'Enter') handleSave(field);
                        }}
                      />
                    ) : (
                      <span>
                        {watchedTranslations[language]?.[field]
                          ? watchedTranslations[language][field]
                            : translations?.[language]?.[field]
                            ? translations[language][field]
                          : ''}
                      </span>
                    )}
                  </td>
                  <td className="actions flex items-center justify-end">
                    {!translations && !watchedTranslations[language]?.[field] && (
                      <Tooltip className="ml-1" title={t('dialogs.generateTranslations.remove')}>
                        <div>
                          <IconButton
                            onClick={() => handleRemove(field)}
                          ><FaTrash /></IconButton>
                        </div>
                      </Tooltip>
                    )}
                    <Fragment>
                      {selectedKey === field ? (
                        <Tooltip className="ml-1" title={t('common.save')}>
                          <div>
                            <IconButton
                              onClick={() => handleSave(field)}
                            ><FaCheck /></IconButton>
                          </div>
                        </Tooltip>
                      ) : (
                        <Tooltip className="ml-1" title={t('dialogs.generateTranslations.edit')}>
                          <div>
                            <IconButton
                              onClick={() => handleEdit(field)}
                            ><FaEdit /></IconButton>
                          </div>
                        </Tooltip>
                      )}
                    </Fragment>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onClose}
          >{t('common.close')}</Button>
          <Button
            className="ml-2"
            variant="outlined"
            loading={isLoading}
            onClick={handleTranslate}
            disabled={isEmptyToTranslate}
          >{t('dialogs.generateTranslations.translate')}</Button>
          <Button
            className="ml-2"
            variant="contained"
            onClick={handleApply}
            disabled={isLoading}
          >{t('common.apply')}</Button>
        </DialogActions>
      </div>
    </Dialog>
  )
}

export default GenerateTranslationsDialog;
